/* AdminBusinessPricelist - Upload en beheer van meerdere zakelijke prijslijsten (PDF).
   Updated: Opslagbestand wordt nu opgeruimd als de database-insert mislakt (geen weesbestanden). */

import { useEffect, useState, useCallback } from 'react';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  FileText, Upload, Trash2, Loader as Loader2, CheckCircle2,
  Power, AlertCircle, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

interface Pricelist {
  id: string;
  filename: string;
  storage_path: string;
  file_size: number | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export default function AdminBusinessPricelist() {
  const [pricelists, setPricelists] = useState<Pricelist[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [startActive, setStartActive] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchPricelists = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('business_pricelists')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPricelists((data || []) as Pricelist[]);
    } catch {
      toast.error('Kon prijslijsten niet ophalen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricelists();
  }, [fetchPricelists]);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecteer een PDF-bestand');
      return;
    }

    if (selectedFile.type !== 'application/pdf' && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Alleen PDF-bestanden zijn toegestaan');
      return;
    }

    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('Bestand is te groot (max 50 MB)');
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const timestamp = Date.now();
      const safeName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `pricelists/${timestamp}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from('business-pricelists')
        .upload(storagePath, selectedFile, {
          contentType: 'application/pdf',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from('business_pricelists')
        .insert({
          filename: selectedFile.name,
          storage_path: storagePath,
          file_size: selectedFile.size,
          description: description.trim() || null,
          is_active: startActive,
          uploaded_by: user?.id,
        });

      if (insertError) {
        await supabase.storage.from('business-pricelists').remove([storagePath]);
        throw insertError;
      }

      toast.success(startActive
        ? 'Prijslijst geüpload en geactiveerd'
        : 'Prijslijst geüpload (inactief)');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setDescription('');
      setStartActive(true);
      fetchPricelists();
    } catch (err: any) {
      toast.error(err.message || 'Uploaden mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (pl: Pricelist) => {
    setTogglingId(pl.id);
    try {
      const { error } = await supabase
        .from('business_pricelists')
        .update({ is_active: !pl.is_active })
        .eq('id', pl.id);

      if (error) throw error;

      toast.success(pl.is_active
        ? 'Prijslijst gedeactiveerd'
        : 'Prijslijst geactiveerd');
      fetchPricelists();
    } catch (err: any) {
      toast.error(err.message || 'Status wijzigen mislukt');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const pricelist = pricelists.find(p => p.id === deleteId);
    if (!pricelist) return;

    setDeleting(true);
    try {
      if (pricelist.storage_path) {
        await supabase.storage.from('business-pricelists').remove([pricelist.storage_path]);
      }

      const { error } = await supabase
        .from('business_pricelists')
        .delete()
        .eq('id', deleteId);

      if (error) throw error;

      toast.success('Prijslijst verwijderd');
      setDeleteId(null);
      fetchPricelists();
    } catch (err: any) {
      toast.error(err.message || 'Verwijderen mislukt');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const activeCount = pricelists.filter(p => p.is_active).length;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <Breadcrumb items={[{ label: 'Zakelijk', path: '/kh-admin/zakelijk/prijslijst' }, { label: 'Prijslijsten' }]} />
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-bold text-[#1e2b22]">Zakelijke prijslijsten</h1>
        <Button
          onClick={() => setShowUploadDialog(true)}
          className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
        >
          <Upload className="h-4 w-4 mr-2" />
          Nieuwe prijslijst
        </Button>
      </div>
      <p className="text-[#5a6552] mb-8">
        Beheer meerdere prijslijsten tegelijk. Je kunt meerdere lijsten tegelijkertijd actief hebben — zakelijke klanten zien alle actieve lijsten.
      </p>

      {/* Summary bar */}
      {pricelists.length > 0 && (
        <div className="flex items-center gap-4 mb-6 text-sm">
          <span className="inline-flex items-center gap-1.5 text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            {activeCount} actief
          </span>
          <span className="inline-flex items-center gap-1.5 text-[#5a6552]">
            <FileText className="h-4 w-4" />
            {pricelists.length - activeCount} inactief
          </span>
        </div>
      )}

      {/* Pricelist list */}
      <div className="bg-white rounded-2xl border border-[#e7e1d6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5a6552]">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : pricelists.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="h-10 w-10 text-[#cfcac2] mx-auto mb-3" />
            <p className="text-[#5a6552]">Nog geen prijslijsten geüpload.</p>
            <p className="text-sm text-[#cfcac2] mt-1">Klik op "Nieuwe prijslijst" om een PDF te uploaden.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#e7e1d6]">
            {pricelists.map((pl) => (
              <div key={pl.id} className="flex items-center justify-between p-5 hover:bg-[#e7e1d6]/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#1e2b22]/5 border border-[#e7e1d6]">
                    <FileText className="h-6 w-6 text-[#1e2b22]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1e2b22] flex items-center gap-2">
                      {pl.filename}
                      {pl.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          Actief
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          Inactief
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-[#5a6552]">
                      PDF · {formatFileSize(pl.file_size)} · {formatDate(pl.created_at)}
                    </p>
                    {pl.description && (
                      <p className="text-sm text-[#5a6552] mt-1">{pl.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggle(pl)}
                    disabled={togglingId === pl.id}
                    className={pl.is_active
                      ? "text-amber-600 hover:bg-amber-50"
                      : "text-green-600 hover:bg-green-50"}
                    title={pl.is_active ? 'Deactiveren' : 'Activeren'}
                  >
                    {togglingId === pl.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Power className="h-4 w-4" />}
                  </Button>
                  {!pl.is_active ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(pl.id)}
                      className="text-red-500 hover:bg-red-50"
                      title="Verwijderen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      className="text-gray-300 cursor-not-allowed"
                      title="Deactiveer eerst voordat je kunt verwijderen"
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload dialog */}
      {showUploadDialog && (
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#1e2b22]">Nieuwe prijslijst uploaden</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file-upload" className="text-sm font-medium text-[#1e2b22]">
                  PDF-bestand <span className="text-red-500">*</span>
                </Label>
                <input
                  id="file-upload"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[#5a6552] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#1e2b22] file:text-white file:font-medium file:cursor-pointer hover:file:bg-[#2a3a2f] file:transition-colors"
                />
                {selectedFile && (
                  <p className="text-xs text-[#5a6552] flex items-center gap-1 mt-1">
                    <FileText className="h-3.5 w-3.5" />
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pl-description" className="text-sm font-medium text-[#1e2b22]">
                  Omschrijving (optioneel)
                </Label>
                <Textarea
                  id="pl-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Bijv. Prijslijst najaar 2026"
                  rows={2}
                  className="resize-none bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1e2b22]">Status na uploaden</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStartActive(true)}
                    className={`flex-1 rounded-lg border p-3 text-sm font-medium transition-colors ${
                      startActive
                        ? 'border-green-300 bg-green-50 text-green-800'
                        : 'border-[#e7e1d6] bg-white text-[#5a6552] hover:bg-[#e7e1d6]/30'
                    }`}
                  >
                    <CheckCircle2 className="h-4 w-4 mx-auto mb-1" />
                    Direct activeren
                  </button>
                  <button
                    type="button"
                    onClick={() => setStartActive(false)}
                    className={`flex-1 rounded-lg border p-3 text-sm font-medium transition-colors ${
                      !startActive
                        ? 'border-gray-300 bg-gray-50 text-gray-700'
                        : 'border-[#e7e1d6] bg-white text-[#5a6552] hover:bg-[#e7e1d6]/30'
                    }`}
                  >
                    <FileText className="h-4 w-4 mx-auto mb-1" />
                    Inactief opslaan
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2 text-xs text-[#5a6552] bg-[#fcfaf6] border border-[#e7e1d6] rounded-lg p-3">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p>Meerdere prijslijsten kunnen tegelijk actief zijn. Actieve prijslijsten zijn zichtbaar voor goedgekeurde zakelijke klanten en kunnen niet worden verwijderd.</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setShowUploadDialog(false); setSelectedFile(null); setDescription(''); setStartActive(true); }}
                className="border-[#e7e1d6] text-[#5a6552]"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Uploaden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <Dialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
          <DialogContent className="max-w-sm border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#1e2b22]">Prijslijst verwijderen</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#5a6552]">
              Weet je zeker dat je deze prijslijst wilt verwijderen? Het bestand wordt permanent verwijderd. Dit kan niet ongedaan worden gemaakt.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#e7e1d6] text-[#5a6552]">
                Annuleren
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
